import { Template } from "@common-models/template";
import { toastDBCreateError } from "@common/utils/db-create-error-toast";
import { AnalyticsService } from "@common-services/analytics-service";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetScheduledMessageEditor } from "./ScheduledMessageEditorReducer";

import {
  campaignReducerActions,
  DBCampaignThunks,
  userMessageReducerActions,
  userMessagesSelectByQuery
} from "@common-reducers/DBServiceThunks";
import { Campaign, CampaignStatus } from "@common/models/campaign-model";
import { buildDuplicateCampaign } from "./campaign-duplicate";
import { CommonRootState } from "@common/types/common-root-state-type";
import moment from "moment";
import { accessFormPopupActions } from "./AccessFormPopupReducer";
import {
  deleteCampaign,
  resumeCampaign
} from "./backend-api/backend-api";
import { ThemeType } from "./WhatsAppReducer";

export type BulkDashboardTab =
  | "audiences"
  | "timeAndDate"
  | "messageAndFiles"
  | "finish"
  | "manageAudiences"
  | "manageTemplates"
  | "manageCampaigns";


export type BulkView =
  | ""
  | "campaignManagementView" // management view
  | "newCampaignView" // new campaign view
  | "campaignBoardFromManagementView" // board from management view
  | "campaignBoardFromNewCampaignView" // board from new campaign view
  | "campaignLogsView" // logs view
  | "campaignNewAudienceFromManagementView" // new audience from management view
  | "campaignNewAudienceFromNewCampaignView" // new audience from new campaign view
  | "campaignNewAudienceFromWhatsappView" // new audience from whatsapp view
  | "campaignNewTemplateViewFromManagement" // new template view from management view
  | "campaignNewTemplateViewFromNewCampaignView" // new template view from new campaign view
  | "campaignNewTemplateViewFromWhatsappView" // new template view from whatsapp view
  | "campaignLogsView" // campaign logs view


export const defaultNewCampaign: Partial<Campaign> = {
  name: "",
  campaignStatus: 'initial',
  boardIDArray: undefined,
  listIndex: '',
  template: undefined,
  templateIndex: '',
  schedule: { isNow: true, dueDate: moment().toISOString(), cancelIfReceived: false },
  timing: {
    messageInterval: 8,
    enforceSendingWindow: false,
    automaticTiming: {
      active: true,
      age: 0,
      activity: 0,
    }
  },
};

export const defaultTemplate: Partial<Template> = {
  name: `New Template`,
  message: "",
  asset: undefined
};

export interface ContactListPanelState {
  selectedListId?: string;
  contextMenuAnchor?: HTMLButtonElement;
}

export type CampaignSortField = 'campaignStatus' | 'createdAt' | 'updatedAt';
export type CampaignSortOrder = 'asc' | 'desc';

export interface CampaignSortOptions {
  field: CampaignSortField;
  order: CampaignSortOrder;
  statusFilter?: CampaignStatus | null;
}

export type BulkLogsViewFilterType = 'bt-sent' | 'received' | 'read' | 'played' | 'failed' | 'pending' | 'not_on_whatsapp' | null;

export interface CampaignDashboardState {
  open: boolean;
  activeTab: BulkDashboardTab;
  paymentPopupOpen: boolean;
  themeType?: ThemeType;
  contactListPanelState: ContactListPanelState;
  newCampaign?: Partial<Campaign>;
  currentTemplate?: Partial<Template>;
  currentTemplateDraft?: Partial<Template>;
  isLoadingDuplicateBoard: boolean;
  isAdvancedModeOpen?: boolean;
  campaignIdForLogsView?: string;
  campaigns: Campaign[];
  isLoadingDuplicateTemplate: boolean;
  activeBulkView: BulkView;
  previousBulkView: BulkView;
  isTemplateDropdownModalOpen: boolean;
  isImportModalOpen: boolean;
  isCampaignLaunching: boolean;
  campaignSortOptions: CampaignSortOptions;
  // Bulk Dashboard UI State
  bulkLogsViewActiveTab: number;
  bulkLogsViewActiveFilter: BulkLogsViewFilterType;
  bulkCampaignDialogSearchTerm: string;
  bulkAudiencesListSearchTerm: string;
  bulkTemplateDialogSearchTerm: string;
  // Audience Validation State
  audienceValidationInProgress: boolean;
  audienceValidationProgress: { current: number; total: number };
  audienceValidationCancelled: boolean;
  audienceValidationResults: {
    valid: number;
    invalid: Array<{ phone: string; reason: string; taskId: string; name?: string; chatId?: string }>;
  } | null;
  previousState: {
    activeTab: BulkDashboardTab;
    activeBulkView: BulkView;
  } | null;
  // BE#658 (4a): a DEDICATED, lightweight delivery-evidence map keyed by messageId — the
  // projected userMessage fields the campaign log view needs to recognise a delivered send on a
  // stuck/stray log trail. Kept OUT of DBUserMessageReducer on purpose: these are partial docs
  // (no content), so they must never shadow the entity adapter the Retry-execution path reads.
  deliveryEvidence: Record<string, DeliveryEvidenceDoc>;
}

export interface DeliveryEvidenceDoc {
  status?: string;
  serverAck?: number;
  waMessageKey?: unknown;
  acknowledged?: boolean;
  // BE#710: carried so the not-on-WhatsApp classifier (isNotOnWhatsAppFailure) can resolve the
  // failure cause off the evidence doc when it is preferred over the log's embedded snapshot.
  sendFailureReason?: string;
  sendFailureError?: unknown;
}

const initialState: CampaignDashboardState = {
  open: false,
  activeTab: "manageCampaigns",
  paymentPopupOpen: false,
  contactListPanelState: {},
  newCampaign: defaultNewCampaign,
  currentTemplate: defaultTemplate,
  currentTemplateDraft: defaultTemplate,
  isLoadingDuplicateBoard: false,
  isAdvancedModeOpen: false,
  campaignIdForLogsView: "",
  campaigns: [],
  isLoadingDuplicateTemplate: false,
  activeBulkView: "campaignManagementView",
  previousBulkView: "",
  isTemplateDropdownModalOpen: false,
  isImportModalOpen: false,
  isCampaignLaunching: false,
  campaignSortOptions: {
    field: 'createdAt',
    order: 'desc'
  },
  deliveryEvidence: {},
  // Bulk Dashboard UI State
  bulkLogsViewActiveTab: 0,
  bulkLogsViewActiveFilter: null,
  bulkCampaignDialogSearchTerm: "",
  bulkAudiencesListSearchTerm: "",
  bulkTemplateDialogSearchTerm: "",
  // Audience Validation State
  audienceValidationInProgress: false,
  audienceValidationProgress: { current: 0, total: 0 },
  audienceValidationCancelled: false,
  audienceValidationResults: null,
  previousState: null,
};



export const resumeCampaignThunk = createAsyncThunk<
  void,
  string,
  { state: CommonRootState }
>("resumeCampaignThunk", (campaignId, thunkApi) => {
  const { dispatch } = thunkApi;

  dispatch(resumeCampaign.initiate(campaignId)).then((res) => {
    if ("data" in res) {
      if (res.data.campaign) {
        dispatch(
          campaignReducerActions.updateOne({
            id: campaignId,
            changes: res.data.campaign,
          })
        );
      }
      if (res.data.campaignMessages?.length > 0) {
        dispatch(
          userMessageReducerActions.upsertMany(res.data.campaignMessages)
        );
      }
    }
  });
});

export const deleteCampaignThunk = createAsyncThunk<
  void,
  string,
  { state: CommonRootState }
>("deleteCampaignThunk", async (campaignId, thunkApi) => {
  const { dispatch } = thunkApi;

  const state = thunkApi.getState();
  const campaignMessages = userMessagesSelectByQuery(
    state.DBUserMessageReducer,
    {
      campaignId,
    }
  );
  dispatch(
    userMessageReducerActions.removeMany(
      campaignMessages.map((message) => message._id)
    )
  );
  dispatch(campaignReducerActions.removeOne(campaignId));
  await dispatch(deleteCampaign.initiate(campaignId));
});

export const duplicateCampaignThunk = createAsyncThunk<
  string | undefined,
  string,
  { state: CommonRootState }
>("duplicateCampaignThunk", async (campaignId, thunkApi) => {
  const { dispatch, getState } = thunkApi;
  const state = getState();

  // Get the original campaign
  const originalCampaign = state.DBCampaignReducer.entities[campaignId];

  if (!originalCampaign) {
    throw new Error("Campaign not found");
  }

  // FE#252: build a FRESH, unlaunched draft copy (never the original's _id, so
  // this creates a new campaign rather than editing the original).
  const copy = buildDuplicateCampaign(originalCampaign, defaultNewCampaign);

  // Actually PERSIST the duplicate. The old code only staged a transient
  // `newCampaign` and never created anything — so "Duplicate" produced no
  // campaign and dropped the user on the wizard with nothing routed. Creating it
  // here is what makes it a real duplicate the user can continue editing.
  const res = await dispatch(DBCampaignThunks.create(copy));
  // #1136: the create REJECTS when the copy was not stored. Say so and stay put rather
  // than opening the wizard on a "duplicate" that does not exist.
  if (DBCampaignThunks.create.rejected.match(res)) {
    toastDBCreateError(res.payload ?? res.error, 'The campaign was not duplicated.');
    return undefined;
  }
  const created = res.payload;

  // Route to the just-duplicated campaign (keyed on its real _id) for continued
  // editing — starting at the audiences step like the edit flow — instead of the
  // old jump to the 'finish' tab of an unsaved copy.
  dispatch(setNewCampaign(created ?? copy));
  dispatch(setActiveBulkView('newCampaignView'));
  dispatch(setActiveTab('audiences'));

  return created?._id;
});

export const editCampaignThunk = createAsyncThunk<
  void,
  string,
  { state: CommonRootState }
>("editCampaignThunk", async (campaignId, thunkApi) => {
  const { dispatch, getState } = thunkApi;
  const state = getState();
  
  // Get the original campaign from entities
  const originalCampaign = state.DBCampaignReducer.entities[campaignId];
  
  if (!originalCampaign) {
    throw new Error("Campaign not found");
  }

  // Load the campaign data for editing (keep the _id for updates)
  // Create a fresh object starting from defaultNewCampaign to clear any leftover data,
  // then override with the campaign's actual data
  const editCampaign: Partial<Campaign> = {
    ...defaultNewCampaign,
    _id: originalCampaign._id,
    name: originalCampaign.name,
    boardIDArray: originalCampaign.boardIDArray,
    template: originalCampaign.template,
    timing: originalCampaign.timing,
    schedule: originalCampaign.schedule,
  };

  // Set the campaign in the state for editing in a single operation
  dispatch(setNewCampaign(editCampaign));
  
  // Also set the current template if it exists, so the editor displays it correctly
  if (originalCampaign.template) {
    dispatch(setCurrentTemplate(originalCampaign.template));
  }
  
  // Navigate to campaign view (start from first tab)
  dispatch(setActiveBulkView('newCampaignView'));
  dispatch(setActiveTab('audiences'));
});


const CampaignSlice = createSlice({
  name: "Campaigns",
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<BulkDashboardTab>) => {
      state.activeTab = action.payload;
    },
    nextTab: (state) => {
      const tabs: BulkDashboardTab[] = [
        "audiences",
        "messageAndFiles",
        "timeAndDate",
        "finish",
      ];
      const currentTabIndex = tabs.indexOf(state.activeTab);
      if (currentTabIndex < tabs.length - 1) {
        state.activeTab = tabs[currentTabIndex + 1];
      }
    },
    openPaymentPopup: (state) => {
      state.paymentPopupOpen = true;
    },
    closePaymentPopup: (state) => {
      state.paymentPopupOpen = false;
    },
    updateBulkDashboardDisplay: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    openBulkDashboard: (state) => {
      state.open = true;
    },
    closeBulkDashboard: (state) => {
      state.open = false;
    },
    updateThemeType: (state, action: PayloadAction<ThemeType>) => {
      state.themeType = action.payload;
    },
    setSelectedBulkListId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.contactListPanelState.selectedListId = action.payload;
    },
    setNewCampaign: (state, action: PayloadAction<Partial<Campaign>>) => {
      state.newCampaign = action.payload;
    },
    setActiveBulkView: (state, action: PayloadAction<BulkView>) => {
      state.activeBulkView = action.payload;
    },
    setCurrentTemplate: (state, action: PayloadAction<Template>) => {
      state.currentTemplate = action.payload;
      state.currentTemplateDraft = { ...action.payload };
      if (state.newCampaign) {
        state.newCampaign = {
          ...state.newCampaign,
          template: { ...action.payload }
        };
      }
    },
    updateCurrentTemplateDraft: (state, action: PayloadAction<Partial<Template>>) => {
      state.currentTemplateDraft = {
        ...state.currentTemplateDraft,
        ...action.payload
      };
    },
    syncDraftToTemplate: (state) => {
      state.currentTemplate = { ...state.currentTemplateDraft };
      if (state.newCampaign) {
        state.newCampaign = {
          ...state.newCampaign,
          template: { ...state.currentTemplateDraft } as Template
        };
      }
    },
    setIsLoadingDuplicateBoard: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDuplicateBoard = action.payload;
    },
    setIsAdvancedModeOpen: (state, action: PayloadAction<boolean>) => {
      state.isAdvancedModeOpen = action.payload;
    },
    setCampaignIdForLogsView: (state, action: PayloadAction<string>) => {
      state.campaignIdForLogsView = action.payload;
    },
    // BE#658 (4a): merge projected delivery-evidence docs (keyed by messageId) into the map.
    // Additive + idempotent; never clears, so it caches across log-view opens.
    mergeDeliveryEvidence: (state, action: PayloadAction<Array<{ _id: string } & DeliveryEvidenceDoc>>) => {
      for (const d of action.payload) {
        if (!d || !d._id) continue;
        state.deliveryEvidence[d._id] = {
          status: d.status,
          serverAck: d.serverAck,
          waMessageKey: d.waMessageKey,
          acknowledged: d.acknowledged,
          sendFailureReason: d.sendFailureReason,
          sendFailureError: d.sendFailureError,
        };
      }
    },
    setCampaigns: (state, action: PayloadAction<Campaign[]>) => {
      state.campaigns = action.payload;
    },
    setIsLoadingDuplicateTemplate: (state, action: PayloadAction<boolean>) => {
      state.isLoadingDuplicateTemplate = action.payload;
    },
    setIsTemplateDropdownModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isTemplateDropdownModalOpen = action.payload;
    },
    setIsImportModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isImportModalOpen = action.payload;
    },
    setIsCampaignLaunching: (state, action: PayloadAction<boolean>) => {
      state.isCampaignLaunching = action.payload;
    },
    resetCampaignAndTemplate: (state) => {
      state.newCampaign = defaultNewCampaign;
      state.currentTemplate = defaultTemplate;
      state.currentTemplateDraft = defaultTemplate;
    },
    setCampaignSortOptions: (state, action: PayloadAction<CampaignSortOptions>) => {
      state.campaignSortOptions = action.payload;
    },
    setBulkLogsViewActiveTab: (state, action: PayloadAction<number>) => {
      state.bulkLogsViewActiveTab = action.payload;
    },
    setBulkLogsViewActiveFilter: (state, action: PayloadAction<BulkLogsViewFilterType>) => {
      state.bulkLogsViewActiveFilter = action.payload;
    },
    setBulkCampaignDialogSearchTerm: (state, action: PayloadAction<string>) => {
      state.bulkCampaignDialogSearchTerm = action.payload;
    },
    setBulkAudiencesListSearchTerm: (state, action: PayloadAction<string>) => {
      state.bulkAudiencesListSearchTerm = action.payload;
    },
    setBulkTemplateDialogSearchTerm: (state, action: PayloadAction<string>) => {
      state.bulkTemplateDialogSearchTerm = action.payload;
    },
    setAudienceValidationInProgress: (state, action: PayloadAction<boolean>) => {
      state.audienceValidationInProgress = action.payload;
    },
    setAudienceValidationProgress: (state, action: PayloadAction<{ current: number; total: number }>) => {
      state.audienceValidationProgress = action.payload;
    },
    setAudienceValidationCancelled: (state, action: PayloadAction<boolean>) => {
      state.audienceValidationCancelled = action.payload;
    },
    setAudienceValidationResults: (state, action: PayloadAction<CampaignDashboardState['audienceValidationResults']>) => {
      state.audienceValidationResults = action.payload;
    },
    resetAudienceValidation: (state) => {
      state.audienceValidationInProgress = false;
      state.audienceValidationProgress = { current: 0, total: 0 };
      state.audienceValidationCancelled = false;
      state.audienceValidationResults = null;
    },
    setPreviousState: (state, action: PayloadAction<CampaignDashboardState['previousState']>) => {
      state.previousState = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(setActiveTab, (state, action) => {
      state.activeTab = action.payload;
      // Send analytics event for setting active tab
      const analyticsService: AnalyticsService = new AnalyticsService();

      analyticsService.event('bulk_dashboard_tab_change', {
        category: 'bulk',
        action: state.activeTab,
      });
    });
    // If launchCampaignThunk throws (DBCampaignThunks.create rejects,
    // MQTT lookup fails, etc.), the manual setIsCampaignLaunching(false)
    // at the end of the thunk body never runs and the Launch button stays
    // disabled forever. Reset the flag here so the user can retry.
    // String literal used because importing the thunk would be circular
    // (CampaignThunks imports from CampaignReducer).
    builder.addCase(
      'campaigns/launchCampaignThunk/rejected',
      (state) => {
        state.isCampaignLaunching = false;
      }
    );
  },
});

export const toggleBulkDashboardDisplay = createAsyncThunk<
  void,
  void,
  { state: CommonRootState }
>("toggleBulkDashboardDisplay", (accessToken, thunkApi) => {
  const state = thunkApi.getState();
  const { dispatch } = thunkApi;
  const hasUser = !!state.UserReducer.userId;

  if (!hasUser) {
    dispatch(accessFormPopupActions.setStatus("Signup"));
  } else {
    dispatch(updateBulkDashboardDisplay(!state.CampaignReducer.open));
  }
});

export const resetCampaignAndTemplateThunk = createAsyncThunk<
  void,
  void,
  { state: CommonRootState }
>("resetCampaignAndTemplateThunk", async (_, thunkApi) => {
  const { dispatch } = thunkApi;
  dispatch(resetCampaignAndTemplate());
  // Also reset the scheduled message editor state
  dispatch(resetScheduledMessageEditor());
});

export const {
  mergeDeliveryEvidence,
  setActiveTab,
  openPaymentPopup,
  closePaymentPopup,
  updateBulkDashboardDisplay,
  openBulkDashboard,
  closeBulkDashboard,
  updateThemeType,
  setSelectedBulkListId,
  setNewCampaign,
  setCurrentTemplate,
  updateCurrentTemplateDraft,
  syncDraftToTemplate,
  setIsLoadingDuplicateBoard,
  setIsAdvancedModeOpen,
  setCampaignIdForLogsView,
  setCampaigns,
  setIsLoadingDuplicateTemplate,
  setActiveBulkView,
  setIsTemplateDropdownModalOpen,
  setIsImportModalOpen,
  nextTab,
  setIsCampaignLaunching,
  resetCampaignAndTemplate,
  setCampaignSortOptions,
  setBulkLogsViewActiveTab,
  setBulkLogsViewActiveFilter,
  setBulkCampaignDialogSearchTerm,
  setBulkAudiencesListSearchTerm,
  setBulkTemplateDialogSearchTerm,
  setAudienceValidationInProgress,
  setAudienceValidationProgress,
  setAudienceValidationCancelled,
  setAudienceValidationResults,
  resetAudienceValidation,
  setPreviousState
} = CampaignSlice.actions;
export default CampaignSlice.reducer;


